import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LINKS_BEFORE = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Experience", href: "#experience" },
];

const LINKS_AFTER = [
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" },
];

const linkClass =
  "text-sm font-medium text-navy/70 transition-colors hover:text-navy";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "border-b border-navy/10 bg-cream/90 backdrop-blur-md"
          : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 font-display text-xl font-semibold tracking-tight"
        >
          <span className="block h-2.5 w-2.5 bg-rust" aria-hidden="true" />
          Kaizen&nbsp;Labs
        </a>

        {/* Desktop nav */}
        <nav
          className="hidden items-center gap-8 lg:flex"
          aria-label="Primary navigation"
        >
          {LINKS_BEFORE.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={linkClass}
            >
              {l.label}
            </a>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-projects-dropdown"
              className={`flex items-center gap-1 outline-none ${linkClass}`}
            >
              Projects <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="border-navy/15">
              <DropdownMenuItem asChild>
                <Link
                  to="/doccare"
                  data-testid="nav-projects-doccare"
                  className="flex cursor-pointer items-center justify-between gap-6"
                >
                  DocCare
                  <ArrowUpRight className="h-3.5 w-3.5 text-rust" />
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {LINKS_AFTER.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={linkClass}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-discuss-project-btn"
            className="hidden rounded-full bg-navy px-6 py-2.5 text-sm font-medium text-cream transition-transform duration-300 hover:-translate-y-0.5 sm:inline-block"
          >
            Discuss Your Project
          </a>

          {/* Mobile nav */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-btn"
                aria-label="Open navigation menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-cream">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2.5 font-display text-xl">
                  <span className="block h-2.5 w-2.5 bg-rust" aria-hidden="true" />
                  Kaizen Labs
                </SheetTitle>
              </SheetHeader>
              <nav
                className="mt-10 flex flex-col gap-1"
                aria-label="Mobile navigation"
              >
                {[...LINKS_BEFORE, ...LINKS_AFTER.slice(0, 0)].map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="border-b border-navy/10 py-4 font-display text-2xl font-medium"
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  to="/doccare"
                  data-testid="mobile-nav-link-doccare"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between border-b border-navy/10 py-4 font-display text-2xl font-medium"
                >
                  Projects — DocCare
                  <ArrowUpRight className="h-5 w-5 text-rust" />
                </Link>
                {LINKS_AFTER.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="border-b border-navy/10 py-4 font-display text-2xl font-medium"
                  >
                    {l.label}
                  </a>
                ))}
              </nav>
              <a
                href="#contact"
                data-testid="mobile-nav-discuss-project-btn"
                onClick={() => setOpen(false)}
                className="mt-8 block rounded-full bg-navy py-3.5 text-center text-sm font-medium text-cream"
              >
                Discuss Your Project
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
